"use client";

import React from "react";
import { ArrowLeft, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SingleTipContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="237647" className="space-y-8 animate-pulse">
        <div className="h-10 w-32 bg-muted rounded-lg"></div>
        <Card className="border-none shadow-xl rounded-3xl">
          <div className="h-[300px] w-full bg-muted rounded-t-3xl"></div>
          <CardHeader className="space-y-4">
            <div className="h-6 w-24 bg-muted rounded-md"></div>
            <div className="h-12 w-3/4 bg-muted rounded-md"></div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="h-4 w-full bg-muted rounded-md"></div>
            <div className="h-4 w-full bg-muted rounded-md"></div>
            <div className="h-4 w-5/6 bg-muted rounded-md"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive font-default text-lg">Failed to load the HomeKit automation tip.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground font-default text-lg">Automation tip not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {posts.map((post, i) => (
        <article key={post.id} data-index={i} className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Button variant="ghost" asChild className="-ml-4 text-muted-foreground hover:text-primary hover:bg-accent rounded-full">
              <Link to="#">
                <ArrowLeft className="mr-2 size-4" />
                Back to Tips & Tricks
              </Link>
            </Button>
          </div>

          <Card className="overflow-hidden border-border shadow-xl bg-card rounded-3xl">
            {}
            {(post.customFields?.image || post.featuredImage) && (
              <div className="w-full max-h-[450px] overflow-hidden bg-muted flex items-center justify-center">
                <img
                  src={post.customFields?.image || post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/homekit_setup_hero.png"}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <CardHeader className="px-6 md:px-12 pt-8 md:pt-12 pb-6">
              {post.customFields?.category && (
                <Badge variant="secondary" className="mb-6 w-fit text-xs px-4 py-1.5 font-default uppercase tracking-wider rounded-full bg-secondary text-secondary-foreground">
                  {post.customFields.category}
                </Badge>
              )}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground leading-tight tracking-tighter">
                {post.title}
              </h1>
            </CardHeader>

            <CardContent className="px-6 md:px-12 py-6 space-y-8">
              <div className="whitespace-pre-wrap text-base md:text-lg text-foreground/80 leading-relaxed font-default">
                {post.customFields?.tip_content || (
                  <span className="italic text-muted-foreground">Our experts are currently drafting this HomeKit guide.</span>
                )}
              </div>

              {post.customFields?.body_image && (
                <div className="w-full overflow-hidden rounded-2xl bg-muted">
                  <img
                    src={post.customFields.body_image}
                    alt={`${post.title} detail`}
                    className="w-full h-auto object-cover"
                  />
                </div>
              )}
            </CardContent>

            <CardFooter className="px-6 md:px-12 pb-8 md:pb-12 pt-6 flex flex-col items-start">
              <Separator className="mb-8 w-full bg-border/60" />
              <div className="flex items-center gap-4 text-muted-foreground">
                <div className="bg-accent p-3 rounded-full border border-border/50">
                  <User className="size-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5 uppercase tracking-widest">Curated by</p>
                  <p className="text-base font-semibold text-foreground font-default">
                    {post.customFields?.author_name || post.author?.name || "Moza Casa Team"}
                  </p>
                </div>
              </div>
            </CardFooter>
          </Card>
        </article>
      ))}
    </div>
  );
}

export default function SingleSmartHomeTipTemplateSection() {
  
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "smart_home_tip" };
      
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-smart-home-tip-template-section" className="bg-background text-foreground py-12 md:py-24 min-h-screen">
      <div className="container mx-auto px-4">
        <PostsProvider wp_query={wp_query}>
          <SingleTipContent />
        </PostsProvider>
      </div>
    </section>
  );
}